import React, { useState } from 'react';
import { Banner, Button, Drawer, Input, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { usersApi } from './usersApi';
import RoleAssignmentsEditor, { assignmentsFromRows, validateRows } from './RoleAssignmentsEditor';

export default function InviteDrawer({ open, roles, properties, onClose, onSent }) {
  const toast = useToast();
  const t = useT();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState('');

  const reset = () => {
    setEmail(''); setFirstName(''); setLastName(''); setAssignments([]); setSent(null); setError('');
  };

  const close = () => {
    if (sending) return;
    reset();
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError(t('Email is required.')); return; }
    const invalid = validateRows(assignments);
    if (invalid) { setError(t(invalid)); return; }
    setError('');
    setSending(true);
    try {
      const result = await usersApi.invite({
        email: email.trim(),
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        roleAssignments: assignmentsFromRows(assignments),
      });
      toast.success(t('Invitation sent'));
      setSent(result);
      onSent?.(result);
    } catch (err) {
      setError(err?.message || t('Could not send invitation.'));
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(sent.acceptUrl);
      toast.success(t('Invite link copied'));
    } catch {
      toast.error(t('Could not copy link'));
    }
  };

  return (
    <Drawer open={open} onClose={close} title={t('Invite user')} size="md">
      {sent ? (
        <div className="set-form">
          <Banner tone="success">{t('Invitation sent to')} <strong>{sent.email}</strong>.</Banner>
          <div className="ps-field">
            <span className="sp-field-label">{t('Invitation link')}</span>
            <p className="set-section__hint">{t('Share this link if email delivery isn’t configured yet. It expires automatically.')}</p>
            <div className="usr-invite-link">
              <code>{sent.acceptUrl}</code>
            </div>
            <Button type="button" variant="secondary" size="sm" leadingIcon={<Icon.Copy size={15} />} onClick={copyLink}>
              {t('Copy link')}
            </Button>
          </div>
          <div className="set-form__actions">
            <Button type="button" variant="ghost" onClick={() => reset()}>{t('Invite another')}</Button>
            <Button type="button" variant="primary" onClick={close}>{t('Done')}</Button>
          </div>
        </div>
      ) : (
        <form className="set-form" onSubmit={submit}>
          {error && <Banner tone="danger">{error}</Banner>}
          <Input label={t('Email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" autoFocus />
          <div className="usr-editor__grid2">
            <Input label={t('First name (optional)')} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            <Input label={t('Last name (optional)')} value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <div>
            <span className="sp-field-label">{t('Roles & access')}</span>
            <RoleAssignmentsEditor value={assignments} onChange={setAssignments} roles={roles} properties={properties} />
          </div>
          <div className="set-form__actions">
            <Button type="button" variant="ghost" onClick={close} disabled={sending}>{t('Cancel')}</Button>
            <Button type="submit" variant="primary" loading={sending}>{t('Send invitation')}</Button>
          </div>
        </form>
      )}
    </Drawer>
  );
}
